import {
    INLINE_EMOJI,
    INLINE_EXPRESSION,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MATH,
    MARK_CODE,
    MARK_KEYBOARD,
} from '@gitbook/doc-core';
import { isTextRangeSelection } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

const InlineControlsArray = [
    INLINE_IMAGE,
    INLINE_EMOJI,
    INLINE_LINK,
    INLINE_MATH,
    INLINE_EXPRESSION,
] as const;

export type InlineControl = (typeof InlineControlsArray)[number];

export type InlineControls = Array<InlineControl>;

/**
 * Plugin to make it easier to interact with inline marks.
 * - Insert a space when arrow right is pressed at the end of a code mark.
 */
export const markPlugin: Plugin = {
    onKeyDown: (event, change) => {
        // If arrow right is pressed.
        if (event.key !== 'ArrowRight') {
            return;
        }

        const { selection } = change.value;

        // If we have a collapsed text selection.
        if (!isTextRangeSelection(selection) || !selection.isCollapsed) {
            return;
        }

        // If we are in a code mark or in a keyboard mark.
        if (
            !change.value.marks.some(
                (mark) => mark.type === MARK_CODE || mark.type === MARK_KEYBOARD
            )
        ) {
            return;
        }

        // If the selection has a focus key.
        if (!selection.focusKey) {
            return;
        }

        const focusedNode = change.value.document.getByKey(selection.focusKey);

        // If the focus node exists.
        if (!focusedNode) {
            return;
        }

        // If we are at the end of the focused node.
        if (!selection.isAtEndOf(focusedNode)) {
            return;
        }

        // Then we insert a space.
        change.removeMark(MARK_CODE).removeMark(MARK_KEYBOARD).insertText(' ');

        event.preventDefault();
    },
};
